"use client"

import type React from "react"

import { useState } from "react"
import { X, GraduationCap, Upload, FileText, CheckCircle } from "lucide-react"
import { Button } from "@/components/ui/button"

interface UpdateGradesModalProps {
  isOpen: boolean
  onClose: () => void
  studentName: string
  onSave: (grades: any, reportCard: File | null) => void
}

const nigerianSubjects = [
  "Mathematics",
  "English Language",
  "Basic Science",
  "Basic Technology",
  "Civic Education",
  "Social Studies",
  "Physical and Health Education",
  "Agricultural Science",
  "Computer Studies",
  "Christian Religious Studies",
  "Islamic Religious Studies",
  "Yoruba Language",
  "Igbo Language",
  "Hausa Language",
  "French",
  "Creative Arts",
  "Home Economics",
]

export function UpdateGradesModal({ isOpen, onClose, studentName, onSave }: UpdateGradesModalProps) {
  const [grades, setGrades] = useState<{ [key: string]: string }>({})
  const [reportCard, setReportCard] = useState<File | null>(null)
  const [showSuccess, setShowSuccess] = useState(false)

  if (!isOpen) return null

  const handleGradeChange = (subject: string, grade: string) => {
    setGrades({ ...grades, [subject]: grade })
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setReportCard(e.target.files[0])
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave(grades, reportCard)
    setShowSuccess(true)
    setTimeout(() => {
      setShowSuccess(false)
      onClose()
    }, 1500)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Update Grades</h2>
              <p className="text-sm text-gray-600">{studentName}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Grades Section */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-4">Subject Grades</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto pr-2">
              {nigerianSubjects.map((subject) => (
                <div key={subject} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
                  <label className="flex-1 text-sm font-medium text-gray-700">{subject}</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="0-100"
                    value={grades[subject] || ""}
                    onChange={(e) => handleGradeChange(subject, e.target.value)}
                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-center"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Upload Report Card Section */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-4">Upload Report Card</h3>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-teal-600 transition-colors">
              <input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={handleFileChange}
                className="hidden"
                id="reportCardUpload"
              />
              <label htmlFor="reportCardUpload" className="cursor-pointer flex flex-col items-center gap-3">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                  <Upload className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Click to upload report card</p>
                  <p className="text-sm text-gray-600 mt-1">PDF, DOC, DOCX, JPG, or PNG (Max 10MB)</p>
                </div>
              </label>
            </div>
            {reportCard && (
              <div className="mt-3 flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <FileText className="w-5 h-5 text-green-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-900">{reportCard.name}</p>
                  <p className="text-xs text-green-700">{(reportCard.size / 1024).toFixed(2)} KB</p>
                </div>
                <button
                  type="button"
                  onClick={() => setReportCard(null)}
                  className="text-red-600 hover:text-red-700 text-sm font-medium"
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          {showSuccess && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <p className="text-sm font-medium text-green-800">Grades updated successfully!</p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 bg-transparent">
              Cancel
            </Button>
            <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
              Save Grades
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
